import { useCallback, useMemo, useState } from 'react';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { Wrapper } from '@styles/CommonStyles';
import { IIStateRegulationFormModel, IStateRegulationModel } from './interfaces';
import { requestUpdateStateRegulation } from './requests';
import { mapOptionTypeIdToValue, StateRegulationModal } from './StateRegulationModal';
import { useStateRequirementsColumns } from './hooks/useStateRequirementsColumns';
import { useStateRegulationConfigurations } from './hooks/useStateRegulationConfigurations';

export const StateRequirements = () => {
  const [editableStateRegulation, setStateRegulation] = useState<IStateRegulationModel | null>(null);
  const [stateRegulationModalPosition, setStateRegulationModalPosition] = useState(0);
  
  const columns = useStateRequirementsColumns();
  const { refetchStateRegulationConfiguration, stateRegulationConfiguration } = useStateRegulationConfigurations();

  const gridActions = useMemo(() => ({ onEdit: (row: IStateRegulationModel) => {
    setStateRegulation(row);
    setStateRegulationModalPosition(prevState => prevState + 1); 
  }}), []);

  const clearEditableStateRegulation = useCallback(() => {
    setStateRegulation(null);
    setStateRegulationModalPosition(0);
  }, []);

  const onSubmit = useCallback(async (values: IIStateRegulationFormModel) => {
    const {
      addStatus,
      dropStatus,
      isOutOfStateTitleAllowed,
      notaryRequiredStatus,
      salesTaxInformation,
      sorRequiredStatus,
      titleHoldingStatus,
      specificRequirements,
      titleDocuments,
      ...restValues
    } = values;

    const requestData = {
      ...restValues,
      addStatus: mapOptionTypeIdToValue(addStatus),
      dropStatus: mapOptionTypeIdToValue(dropStatus),
      isOutOfStateTitleAllowed: mapOptionTypeIdToValue(isOutOfStateTitleAllowed),
      notaryRequiredStatus: mapOptionTypeIdToValue(notaryRequiredStatus),
      sorRequiredStatus: mapOptionTypeIdToValue(sorRequiredStatus),
      titleHoldingStatus: mapOptionTypeIdToValue(titleHoldingStatus),
      salesTaxInformation: salesTaxInformation.html,
      specificRequirements: specificRequirements.html,
      titleDocuments: titleDocuments.html,
    };

    await requestUpdateStateRegulation(requestData);
    refetchStateRegulationConfiguration();
    clearEditableStateRegulation();
  }, []);

  return (
    <Wrapper>
      <StateRegulationModal
        stateRegulationModalPosition={stateRegulationModalPosition}
        stateRegulation={editableStateRegulation}
        onClose={clearEditableStateRegulation}
        onSubmit={onSubmit}
      />
      <PhoenixGrid
        autoHeight
        canNotCopy
        noButton
        strict
        withHeader
        isFullHeightColumn
        showClearAllFilter
        actions={gridActions}
        columns={columns}
        getRowHeight={() => 'auto'}
        name="State Regulation Configurations"
        rows={stateRegulationConfiguration}
      />
    </Wrapper>
  );
};
